/**
 * ContributionsPage — super-admin payroll-facing report of admin/sub-admin
 * contributions.
 *
 * A contribution is defined narrowly: one row per question whose
 * "Mark as Answered" flag was flipped false → true by that admin. Bulk
 * uploads credit the uploader (marked with a `source: bulk` badge in the
 * drill-down).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ArrowLeft, ChevronDown, ChevronRight, ClipboardCheck, Download, Filter, Loader2, RefreshCw, User as UserIcon, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ---------- Time preset helpers ----------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

const presetToRange = (key) => {
  const now = new Date();
  if (key === 'this_week') {
    const d = new Date(now);
    const day = d.getDay(); // 0=Sun
    const diff = (day + 6) % 7; // start Monday
    d.setDate(d.getDate() - diff);
    return { start: startOfDay(d), end: endOfDay(now) };
  }
  if (key === 'this_month') {
    return { start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), end: endOfDay(now) };
  }
  if (key === 'last_month') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last  = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: startOfDay(first), end: endOfDay(last) };
  }
  if (key === 'last_30_days') {
    const d = new Date(now); d.setDate(d.getDate() - 30);
    return { start: startOfDay(d), end: endOfDay(now) };
  }
  if (key === 'all_time') return { start: null, end: null };
  return { start: null, end: null };
};

const isoOrEmpty = (d) => (d ? d.toISOString() : '');

const toLocalInput = (d) => {
  // ISO → 'YYYY-MM-DDTHH:mm' for <input type=datetime-local>
  if (!d) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// ---------- Per-entity summary table ----------
// Expandable per-admin rows: parent row shows totals across all grades; the
// child rows (one per grade) surface how the count splits across MEO Cl 2 /
// MEO Cl 1 / Chief Mate / etc. — needed because the payscale differs per
// grade. `gradeRows` is the raw `by_admin_grade` from the backend.
const SummaryTable = ({ label, rows, gradeRows, accentColor, onDrill }) => {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (adminId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(adminId)) next.delete(adminId); else next.add(adminId);
      return next;
    });
  };

  // Group grade rows by admin_id for this entity type — O(N) once per render.
  const gradesByAdmin = useMemo(() => {
    const map = new Map();
    (gradeRows || [])
      .filter((g) => g.entity_type === label)
      .forEach((g) => {
        if (!map.has(g.admin_id)) map.set(g.admin_id, []);
        map.get(g.admin_id).push(g);
      });
    // Sort each admin's grades by total desc so the biggest bucket shows first.
    map.forEach((arr) => arr.sort((a, b) => b.total - a.total));
    return map;
  }, [gradeRows, label]);

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base capitalize">{label}</CardTitle>
          <Badge variant="outline" className="text-xs">
            {rows.reduce((s, r) => s + r.total, 0)} total
          </Badge>
        </div>
        <CardDescription className="text-xs">Click a row to open the per-question drill-down. Use the arrow to expand per-grade totals.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-400">No contributions in this window.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-6"></th>
                  <th className="text-left px-3 py-2 font-medium">Admin</th>
                  <th className="text-right px-3 py-2 font-medium">UI</th>
                  <th className="text-right px-3 py-2 font-medium">Bulk</th>
                  <th className="text-right px-3 py-2 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const grades = gradesByAdmin.get(r.admin_id) || [];
                  const isOpen = expanded.has(r.admin_id);
                  const hasGrades = grades.length > 0;
                  return (
                    <React.Fragment key={`${r.admin_id}-${label}`}>
                      <tr
                        className="border-t hover:bg-gray-50"
                        data-testid={`contrib-row-${label}-${r.admin_id}`}
                      >
                        <td className="px-3 py-2">
                          {hasGrades ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggle(r.admin_id); }}
                              className="text-gray-400 hover:text-gray-700"
                              aria-label={isOpen ? 'Collapse grade breakdown' : 'Expand grade breakdown'}
                              data-testid={`contrib-expand-${label}-${r.admin_id}`}
                            >
                              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          ) : <span className="inline-block w-4" />}
                        </td>
                        <td className="px-3 py-2 cursor-pointer" onClick={() => onDrill(r)}>
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full ${accentColor} flex items-center justify-center shrink-0`}>
                              <UserIcon className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 truncate">{r.admin_name}</div>
                              {r.admin_indos && <div className="text-[11px] text-gray-500 truncate">{r.admin_indos}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700 cursor-pointer" onClick={() => onDrill(r)}>{r.ui || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700 cursor-pointer" onClick={() => onDrill(r)}>{r.bulk || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold cursor-pointer" onClick={() => onDrill(r)}>{r.total}</td>
                      </tr>
                      {isOpen && hasGrades && grades.map((g) => (
                        <tr
                          key={`${g.admin_id}-${label}-${g.grade}`}
                          className="bg-gray-50/60 border-t border-dashed cursor-pointer hover:bg-gray-100"
                          onClick={() => onDrill({ ...r, grade: g.grade })}
                          data-testid={`contrib-grade-row-${label}-${r.admin_id}-${g.grade}`}
                        >
                          <td></td>
                          <td className="px-3 py-1.5 pl-10 text-xs text-gray-600">
                            <span className="inline-block px-2 py-0.5 rounded bg-white border text-gray-700">{g.grade}</span>
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs text-gray-600">{g.ui || 0}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs text-gray-600">{g.bulk || 0}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs font-medium text-gray-800">{g.total}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------- Drill-down modal ----------
const DrillDownModal = ({ open, onClose, admin, entityType, range, sourceFilter, setSourceFilter, gradeFilter }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 100;

  const fetchDetail = useCallback(async () => {
    if (!admin) return;
    setLoading(true);
    try {
      const params = {
        admin_id: admin.admin_id,
        entity_type: entityType,
        page, per_page: perPage,
      };
      if (range?.start) params.start = range.start.toISOString();
      if (range?.end) params.end = range.end.toISOString();
      if (sourceFilter && sourceFilter !== 'all') params.source = sourceFilter;
      if (gradeFilter) params.grade = gradeFilter;
      const { data } = await axios.get(`${API}/admin/contributions/detail`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load contributions');
    } finally {
      setLoading(false);
    }
  }, [admin, entityType, range, page, sourceFilter, gradeFilter]);

  useEffect(() => {
    if (open) { setPage(1); fetchDetail(); }
  }, [open, admin?.admin_id, entityType, range?.start, range?.end, sourceFilter, gradeFilter, fetchDetail]);

  useEffect(() => { if (open) fetchDetail(); }, [page, open, fetchDetail]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-teal-600" />
            {admin?.admin_name} — {entityType} contributions
          </DialogTitle>
          <p className="text-xs text-gray-500">
            {admin?.admin_indos && <>INDoS: <span className="font-mono">{admin.admin_indos}</span> · </>}
            {gradeFilter && <>Grade: <span className="font-semibold text-gray-700">{gradeFilter}</span> · </>}
            {total} entries in the current time window
          </p>
        </DialogHeader>

        <div className="flex items-center gap-3 mb-3">
          <Label className="text-xs">Source</Label>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="drill-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="ui">UI only</SelectItem>
              <SelectItem value="bulk">Bulk only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">No entries match this filter.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="drill-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Grade</th>
                  <th className="text-left px-3 py-2 font-medium">Question</th>
                  <th className="text-left px-3 py-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap text-xs">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap text-xs">
                      {r.grade || <span className="text-gray-400 italic">Unknown</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      <div className="line-clamp-2" title={r.entity_title || ''}>{r.entity_title || <span className="text-gray-400 italic">Untitled</span>}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{r.entity_id}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-xs ${r.source === 'bulk' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
                        {r.source}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > perPage && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t">
            <p className="text-xs text-gray-500">Page {page} of {Math.ceil(total / perPage)}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / perPage)} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Main page ----------
const ContributionsPage = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');

  const range = useMemo(() => {
    if (preset === 'custom') {
      const start = customStart ? new Date(customStart) : null;
      const end = customEnd ? new Date(customEnd) : null;
      return { start, end };
    }
    return presetToRange(preset);
  }, [preset, customStart, customEnd]);

  const [summary, setSummary] = useState({ oral: [], written: [], by_admin_grade: [], totals: { oral: 0, written: 0 } });
  const [gradeOptions, setGradeOptions] = useState([]);
  const [gradeFilter, setGradeFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Drill-down state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillAdmin, setDrillAdmin] = useState(null);
  const [drillEntity, setDrillEntity] = useState('oral');
  const [drillSource, setDrillSource] = useState('all');
  // Grade context for the drill-down. When the admin clicks the parent row
  // this stays `null` (drill shows all grades); when they click a per-grade
  // sub-row we scope the drill to that grade.
  const [drillGrade, setDrillGrade] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.start) params.start = isoOrEmpty(range.start);
      if (range.end) params.end = isoOrEmpty(range.end);
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (gradeFilter !== 'all') params.grade = gradeFilter;
      const { data } = await axios.get(`${API}/admin/contributions/summary`, { params });
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load contribution summary');
    } finally {
      setLoading(false);
    }
  }, [range, entityFilter, gradeFilter]);

  // Load the distinct grade list once — the dropdown never changes shape
  // within a session unless a new grade contribution lands.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/contributions/grades`);
        setGradeOptions(data.grades || []);
      } catch {
        // Non-fatal — filter just stays as "All grades".
      }
    })();
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const openDrill = (row, entityType) => {
    setDrillAdmin(row);
    setDrillEntity(entityType);
    setDrillSource('all');
    // If the row carries a `grade` (per-grade sub-row click) scope the drill
    // to it. Otherwise inherit the page-level grade filter, falling back to
    // null (all grades).
    setDrillGrade(row?.grade || (gradeFilter !== 'all' ? gradeFilter : null));
    setDrillOpen(true);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (range.start) params.start = range.start.toISOString();
      if (range.end) params.end = range.end.toISOString();
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (gradeFilter !== 'all') params.grade = gradeFilter;
      const res = await axios.get(`${API}/admin/contributions/export.csv`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `contributions_${ts}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const rangeLabel = useMemo(() => {
    if (!range.start && !range.end) return 'All time';
    const s = range.start ? range.start.toLocaleDateString() : '—';
    const e = range.end ? range.end.toLocaleDateString() : '—';
    return `${s} → ${e}`;
  }, [range]);

  return (
    <Layout>
      <div className="page-transition">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/tools')} className="text-gray-600" data-testid="contrib-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Tools
          </Button>
        </div>

        <Card className="mb-6 border-l-4 border-l-teal-500 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-teal-500/5 to-emerald-500/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-teal-600 rounded-lg">
                <ClipboardCheck className="w-7 h-7 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl text-teal-700">Contribution Report</CardTitle>
                <CardDescription className="text-sm mt-1">
                  Every question an admin marked answered — for payroll and audit. Bulk uploads credit the uploader.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Filters */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Filter className="w-4 h-4" /> Filters</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-gray-600">Time window</Label>
                <Select value={preset} onValueChange={setPreset}>
                  <SelectTrigger data-testid="contrib-time-preset"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this_week">This week</SelectItem>
                    <SelectItem value="this_month">This month</SelectItem>
                    <SelectItem value="last_month">Last month</SelectItem>
                    <SelectItem value="last_30_days">Last 30 days</SelectItem>
                    <SelectItem value="all_time">All time</SelectItem>
                    <SelectItem value="custom">Custom range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Entity type</Label>
                <Select value={entityFilter} onValueChange={setEntityFilter}>
                  <SelectTrigger data-testid="contrib-entity-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Both</SelectItem>
                    <SelectItem value="oral">Orals only</SelectItem>
                    <SelectItem value="written">Written only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Grade</Label>
                <Select value={gradeFilter} onValueChange={setGradeFilter}>
                  <SelectTrigger data-testid="contrib-grade-filter"><SelectValue placeholder="All grades" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All grades</SelectItem>
                    {gradeOptions.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {preset === 'custom' && (
                <>
                  <div>
                    <Label className="text-xs text-gray-600">From</Label>
                    <Input type="datetime-local" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="contrib-custom-start" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">To</Label>
                    <Input type="datetime-local" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="contrib-custom-end" />
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 items-center">
              <Button variant="outline" size="sm" onClick={fetchSummary} disabled={loading} data-testid="contrib-refresh-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting || (summary.totals.oral + summary.totals.written === 0)} data-testid="contrib-export-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export CSV
              </Button>
              <div className="ml-auto text-xs text-gray-500">
                Window: <span className="font-medium text-gray-700">{rangeLabel}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary tables */}
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(entityFilter === 'all' || entityFilter === 'oral') && (
              <div data-testid="contrib-oral-table">
                <SummaryTable
                  label="oral"
                  rows={summary.oral}
                  gradeRows={summary.by_admin_grade}
                  accentColor="bg-[#0A7EA4]"
                  onDrill={(row) => openDrill(row, 'oral')}
                />
              </div>
            )}
            {(entityFilter === 'all' || entityFilter === 'written') && (
              <div data-testid="contrib-written-table">
                <SummaryTable
                  label="written"
                  rows={summary.written}
                  gradeRows={summary.by_admin_grade}
                  accentColor="bg-purple-600"
                  onDrill={(row) => openDrill(row, 'written')}
                />
              </div>
            )}
          </div>
        )}

        <DrillDownModal
          open={drillOpen}
          onClose={() => setDrillOpen(false)}
          admin={drillAdmin}
          entityType={drillEntity}
          range={range}
          sourceFilter={drillSource}
          setSourceFilter={setDrillSource}
          gradeFilter={drillGrade}
        />
      </div>
    </Layout>
  );
};

export default ContributionsPage;
